import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { TextField, Chip, Box } from '@mui/material';

import { MultiSelectProps } from './types';

export const MultiSelect = ({ f, validate }: MultiSelectProps) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const {
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    name: f,
    control,
  });

  const error = typeof errors[f]?.message === 'string' ? errors[f]?.message : undefined;
  const helperText = t('common.form.pressEnterToAdd');

  return (
    <TextField
      sx={{
        '& .MuiInputBase-root': {
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        },
        '& .MuiInputBase-input': {
          flex: 1,
        },
      }}
      value={input}
      onChange={(e) => setInput(e.target.value)}
      label={t(`agencies.settings.envMap.${String(f)}`)}
      size="small"
      fullWidth
      helperText={error ?? helperText}
      margin="normal"
      color={error ? 'error' : 'primary'}
      error={Boolean(error)}
      InputProps={{
        startAdornment: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              padding: (theme) => theme.spacing(1, 0.5, 1, 0),
            }}
          >
            {fields.map((field: Record<string, string>, idx) => (
              <Chip key={field.id} label={field.value} onDelete={() => remove(idx)} color="primary" />
            ))}
          </Box>
        ),
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && input) {
          e.preventDefault();

          const items = fields.map((f: Record<string, string>) => ({ value: f.value }));

          const validation = validate && validate(input, items);

          if (typeof validation === 'string') {
            setError(f, { type: 'manual', message: validation });
            return;
          }

          clearErrors(f);

          append({ value: input });
          setInput('');
        }
      }}
    />
  );
};
